import type { ApiResponse, Covers, ReadModel, Sparse } from '../../core/api/adapters';
import type {
  IssueForOptimizedTableDetail,
  OptimizedTableDetail,
  TableDataCellForOptimizedTableDetail,
  TableForUpdate,
  TableSearchItem,
} from '../../core/api/schema';
import type {
  IAttachedTable,
  ICellSearchItem,
  IDataCellItem,
  HyperLink,
  IProjectTag,
  IRowItem,
  ITableIssue,
  ITableItem,
  IUpdatedTableItem,
  TableId,
} from '../../project/models';
import type { ISearchItem } from '../../search/models/search';
import type { SlimUser } from '../../user/models/user';
import { isDefined } from './is-defined.util';

export type ITableBackendResponse = ReadModel<OptimizedTableDetail, TableId>;

export type ITableUpdateBackendResponse = ReadModel<TableForUpdate, TableId>;

export type IResDataCellItem = TableDataCellForOptimizedTableDetail;

export const convertSearchItem = (
  backSearchItem: TableSearchItem | null | undefined,
  cellSearchItem: IResDataCellItem['larisearch_item'],
  issues: ITableIssue[],
  tables: IAttachedTable[],
): ICellSearchItem | null => {
  if (!cellSearchItem || !backSearchItem) {
    return null;
  }
  const li = backSearchItem;
  const headings = cellSearchItem.headings ?? '';
  let head;
  if (li.sections && typeof headings === 'string' && headings in li.sections) {
    head = li.sections[headings];
  } else {
    head = {};
  }
  // Backend-to-frontend conversion: the produced item strips `sections` and resolves the
  // `attached_to_*` url references into domain objects (hence the ICellSearchItem return
  // type). The `as` bridges one remaining divergence: `updated_on` is set null here, and
  // `ISearchItem` types it `string | undefined`.
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- `updated_on` is set null here, per the comment above
  return {
    ...li,
    ...head,
    updated_on: li.is_update && li.timestamp ? li.timestamp.split(' ', 1)[0] : null,
    sections: undefined,
    attached_to_issue: li.attached_to_issue?.map(url => issues.find(i => i.url === url)).filter((i): i is ITableIssue => Boolean(i)),
    attached_to_table: li.attached_to_table?.map(url => tables.find(t => t.url === url)).filter((t): t is IAttachedTable => Boolean(t)),
  } as ICellSearchItem;
};

type IWireRowItem = Exclude<ApiResponse<'tablerow_create'>, readonly unknown[]>;

type IWireDataCellItem = Covers<ApiResponse<'tabledatacell_update'>, IWireRowItem['datacells'][number]>;

/** The read-model cell fields a write answer does not carry, so the writer has to state them. */
type ResolvedCellField = Exclude<keyof IResDataCellItem, keyof IWireDataCellItem>;

export type IWrittenDataCellItem = Omit<IDataCellItem, ResolvedCellField>;

const convertHyperlink = (hyperlink: IWireDataCellItem['hyperlink'] | undefined): HyperLink | null =>
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- the FE `HyperLink` is deliberately narrower than the wire type
  (hyperlink ?? null) as HyperLink | null;

export const writtenCell = (cell: IWireDataCellItem): IWrittenDataCellItem =>
  // A patch merges into the stored cell, so an absent `hyperlink` must stay absent rather than clobber it.
  'hyperlink' in cell ? { ...cell, hyperlink: convertHyperlink(cell.hyperlink) } : cell;

export type IWrittenRowItem = IRowItem<IWrittenDataCellItem>;

/** The four a created row's writer states; nothing in the app produces a cell `report_issue`. */
type StatedRowCellField = Extract<ResolvedCellField, 'larisearch_item' | 'tags' | 'users' | 'issue'>;

/**
 * The {@link StatedRowCellField}s of a created row as its writer holds them — keyed by datacolumn,
 * because a row it just created has no cell ids to match on. An absent field is "not stated", not
 * "cleared".
 */
export interface IResolvedRowCell extends Sparse<Pick<IDataCellItem, StatedRowCellField>> {
  datacolumn: string;
}

export const writtenRow = ({ datacells, ...row }: IWireRowItem): IWrittenRowItem => ({
  ...row,
  datacells: datacells.map(writtenCell),
});

/** Fills the fields a row create does not answer with: the writer states, per datacolumn, what it wrote. */
export const rowWithResolved = (row: IWrittenRowItem, resolved: readonly IResolvedRowCell[]): IRowItem => {
  const byDatacolumn = new Map(resolved.map(cell => [cell.datacolumn, cell]));
  return {
    ...row,
    datacells: row.datacells.map(cell => {
      const source = byDatacolumn.get(cell.datacolumn);
      if (source === undefined) {
        return cell;
      }
      const { larisearch_item: hit, tags, users, issue } = source;
      return {
        ...cell,
        ...(hit !== undefined && { larisearch_item: hit }),
        ...(tags !== undefined && { tags }),
        ...(users !== undefined && { users }),
        ...(issue !== undefined && { issue }),
      };
    }),
  };
};

// A picked item as a table cell holds it. `attached_to_issue` goes: the pick carries the backend's
// slim `{ id, title, url }` embed, and only the table resolves those into the cell's `ITableIssue`s.
export const cellHitFromPick = (item: ISearchItem, attachedTo: IAttachedTable): ICellSearchItem => ({
  ...item,
  attached_to_issue: undefined,
  attached_to_table: [...(item.attached_to_table ?? []).filter(t => t.id !== attachedTo.id), attachedTo],
});

export const convertIssue = (
  issue: IssueForOptimizedTableDetail,
  tags: Record<string, IProjectTag>,
  users: Record<string, SlimUser>,
): ITableIssue => ({
  ...issue,
  id: issue.id,
  url: issue.url,
  title: issue.title ?? '',
  tags: issue.tags?.map(t => tags[t]).filter((t): t is IProjectTag => t !== undefined) ?? [],
  assignee: issue.assignee ? users[issue.assignee] : undefined,
});

export const convertTable = (table: ITableBackendResponse): ITableItem => {
  const { rows, owners, datacells, larisearch_items, datacolumns, project, columns, external_destinations, ...rest } = table;
  // Wire→view boundary. Ids arrive branded from the spec (`x-brand`), so the map embeds
  // (`users`/`tags`/`tables`), `project`/`external_destinations` and the row/cell pks assign directly.
  const users: Record<string, SlimUser> = table.users;
  const tags: Record<string, IProjectTag> = table.tags;
  const tables: Record<string, IAttachedTable> = table.tables;
  const issues = Object.values(table.issues).map(i => convertIssue(i, tags, users));
  const tablesList = Object.values(tables);

  return {
    ...rest,
    project,
    columns,
    external_destinations,
    datacolumns: Object.values(datacolumns),
    owners: owners.map(o => users[o]).filter(isDefined),
    tables,
    users,
    rows: Object.values(rows).map(r => {
      const { datacells: backDataCells, ...restRow } = r;
      return {
        ...restRow,
        datacells: backDataCells
          .map(datacellURL => datacells[datacellURL])
          .filter(isDefined)
          .map(dc => {
            const { larisearch_item } = dc;

            const issue = dc.issue ? issues.find(i => i.url === dc.issue) : null;

            return {
              ...dc,
              users: dc.users?.map(u => users[u]).filter(isDefined) ?? [],
              tags: dc.tags?.map(t => tags[t]).filter(isDefined) ?? [],
              issue: issue ?? null,
              larisearch_item: larisearch_item?.id
                ? convertSearchItem(larisearch_items[larisearch_item.id], larisearch_item, issues, tablesList)
                : null,
              hyperlink: convertHyperlink(dc.hyperlink),
            };
          }),
      };
    }),
  };
};

// A table write answers with `datacolumns` in their brief form; the store holds the optimized one,
// so the reducers' spread merges would degrade it.
export const convertUpdatedTable = ({ datacolumns: _datacolumns, ...table }: ITableUpdateBackendResponse): IUpdatedTableItem => table;
